"use client";

import {
  AspectRatio,
  AssetInViewportAnimation,
  WithoutMotionProps,
} from "./global";
import Image from "next/image";
import { ImageOverlay, ImageOverlayProps } from "./ImageOverlay";
import { AiDisclaimerForAlt, type AiDisclaimerProps } from "./AiDisclaimer";
import { ComponentProps } from "react";
import { cn } from "@repo/utils/cn";
import { parseAiAlt } from "@repo/ai-disclaimer";
import { withStoryblokParams } from "@repo/utils/storyblokLoader";
import { motion } from "motion/react";

export type ResponsiveImageProps = {
  image:
    | (Omit<ComponentProps<typeof Image>, "src" | "alt"> & {
        src: ComponentProps<typeof Image>["src"] | null;
        alt?: ComponentProps<typeof Image>["alt"] | null;
        focus?: string | null;
      })
    | null;
  overlay?: ImageOverlayProps | null;
  aspectRatio?: AspectRatio | null;
  /**
   * Aspect ratio (width / height) of the box this image is painted into, when
   * that box is NOT the image's own shape — i.e. `object-cover` fills. Passed
   * through to `storyblokLoader`, which turns it into a real `m/{w}x{h}` crop
   * so cover has enough vertical pixels to work with. Omit it and the emitted
   * URL is byte-identical to not passing it at all.
   */
  ar?: number | null;
  highDpi?: boolean;
  quality?: ComponentProps<typeof Image>["quality"];
  sizes?: ComponentProps<typeof Image>["sizes"];
  imgClassName?: string;
  inViewportAnimation?: AssetInViewportAnimation | null;
  disableAnimation?: boolean;
  priority?: boolean;
  /**
   * Suppress the AI badge, because an ancestor renders it instead.
   *
   * Only `AnimatedMedia` sets this: it stacks two of these inside its own scaling
   * layers, so a badge rendered here would both scale with the transition and appear
   * twice mid-crossfade. Anything else leaving it out keeps the badge.
   */
  hideAiDisclaimer?: boolean;
  /** `compact` where the image renders as a thumbnail — see `AiDisclaimerProps["size"]`. */
  aiBadgeSize?: AiDisclaimerProps["size"];
} & WithoutMotionProps<ComponentProps<"div">>;

export function ResponsiveImage({
  image,
  aspectRatio,
  overlay,
  ar,
  highDpi,
  quality,
  sizes,
  className,
  imgClassName,
  inViewportAnimation,
  disableAnimation,
  priority = false,
  hideAiDisclaimer = false,
  aiBadgeSize,
  ...props
}: ResponsiveImageProps) {
  if (!image?.src) return null;

  const { src, alt, focus, style: focalStyle, ...imageProps } = image;

  // The AI marker rides at the end of the alt text and must never reach the DOM.
  // Only `cleanAlt` is needed here now: whether a badge is due is decided by
  // `AiDisclaimerForAlt`, which also consults the marked-asset registry.
  const { cleanAlt } = parseAiAlt(alt);
  const animationEasing = [0.01, 0.0, 0.2, 1.0];
  const animationDuration = 0.8;

  const viewportOptions = {
    once: true,
    margin: "-10% 0px -10% 0px",
  };

  const motionProps =
    inViewportAnimation && !disableAnimation
      ? {
          initial: {
            opacity: 0,
            scale: inViewportAnimation === "zoomUp" ? 1.1 : 1,
          },
          whileInView: {
            opacity: 1,
            scale: inViewportAnimation === "zoomUp" ? 1 : 1.1,
          },
          viewport: viewportOptions,
          transition: {
            duration: animationDuration,
            ease: animationEasing,
          },
        }
      : {};

  return (
    <div
      {...props}
      className={cn("ui:relative", className)}
      style={{ aspectRatio: aspectRatio || undefined, ...props.style }}
    >
      {/*
        The animation wraps the picture alone, not the whole box.
        Running it on the container scaled the badge with it — a `zoomUp` settles at
        `scale: 1.1`, which pushes a corner-pinned badge past the card's `overflow-hidden`
        and slices it. The overlay and the badge are siblings of this wrapper for the same
        reason the ParkMobile cards keep them outside their hover-zoom wrapper: only the
        image is meant to move.
      */}
      <motion.div className="ui:absolute ui:inset-0" {...motionProps}>
        <Image
          priority={priority}
          src={
            typeof src === "string"
              ? withStoryblokParams(src, {
                  focus: ar ? focus : undefined,
                  ar,
                  highDpi,
                })
              : src
          }
          alt={cleanAlt}
          fill={true}
          {...imageProps}
          // `focalStyle` is a CSS `objectPosition` percentage computed against the
          // ORIGINAL, uncropped image. When `ar` requests a real server-side crop,
          // Storyblok's `focal()` (just above) already centers the delivered file
          // on the focus point in the CROPPED file's own coordinate space —
          // re-applying the original-image percentage on top would double-count
          // that centering and shift the crop off-target. Only apply it when no
          // server crop is requested, which is every consumer this PR doesn't
          // touch.
          style={ar ? undefined : focalStyle}
          quality={quality}
          sizes={sizes}
          className={cn(imgClassName, imageProps.className)}
        />
      </motion.div>
      {overlay && <ImageOverlay {...overlay} />}
      {!hideAiDisclaimer && (
        <AiDisclaimerForAlt
          alt={alt}
          src={typeof src === "string" ? src : null}
          size={aiBadgeSize}
        />
      )}
    </div>
  );
}
